const activeZoomOverlays = new WeakMap();
const mobileZoomStates = new WeakMap();
const DESKTOP_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const MOBILE_MEDIA_QUERY = '(hover: none) and (pointer: coarse)';
const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 3;
const TAP_MOVE_THRESHOLD = 8;
const TAP_SCROLL_THRESHOLD = 6;
const TAP_ZOOM_SCALE = 2;
const ZOOM_TRANSITION = 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function distance(touchA, touchB) {
  return Math.hypot(touchA.clientX - touchB.clientX, touchA.clientY - touchB.clientY);
}

function getMidPoint(touchA, touchB) {
  return {
    x: (touchA.clientX + touchB.clientX) / 2,
    y: (touchA.clientY + touchB.clientY) / 2,
  };
}

function prepareOverlay(image) {
  const overlay = document.createElement('div');
  overlay.setAttribute('class', 'image-magnify-full-size');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.backgroundImage = `url('${image.currentSrc || image.src}')`;
  overlay.style.backgroundColor = 'var(--gradient-background)';
  return overlay;
}

function getOrCreateOverlay(image) {
  let overlay = activeZoomOverlays.get(image);
  if (overlay) return overlay;

  overlay = prepareOverlay(image);
  image.parentElement.insertBefore(overlay, image);
  activeZoomOverlays.set(image, overlay);
  return overlay;
}

function removeOverlay(image) {
  const overlay = activeZoomOverlays.get(image);
  if (overlay) overlay.remove();
  activeZoomOverlays.delete(image);
}

function updateOverlayPosition(image, event, zoomRatio) {
  const overlay = activeZoomOverlays.get(image);
  if (!overlay) return;

  const rect = image.getBoundingClientRect();
  const x = clamp(event.clientX - rect.left, 0, image.clientWidth);
  const y = clamp(event.clientY - rect.top, 0, image.clientHeight);

  overlay.style.backgroundPosition = `${(x / image.clientWidth) * 100}% ${(y / image.clientHeight) * 100}%`;
  overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
}

function createDefaultMobileState() {
  return {
    scale: 1,
    translateX: 0,
    translateY: 0,
    panStartX: 0,
    panStartY: 0,
    startTranslateX: 0,
    startTranslateY: 0,
    startDistance: 0,
    startScale: 1,
    didPan: false,
    movedTouch: false,
    startScrollY: 0,
  };
}

function getMaxTranslate(image, scale) {
  const maxX = ((scale - 1) * image.clientWidth) / 2;
  const maxY = ((scale - 1) * image.clientHeight) / 2;
  return { maxX, maxY };
}

function applyMobileTransform(image, state, animated = false) {
  const { maxX, maxY } = getMaxTranslate(image, state.scale);
  state.translateX = clamp(state.translateX, -maxX, maxX);
  state.translateY = clamp(state.translateY, -maxY, maxY);

  image.style.transition = animated ? ZOOM_TRANSITION : 'none';
  image.style.transformOrigin = 'center center';
  image.style.transform = `translate3d(${state.translateX}px, ${state.translateY}px, 0) scale(${state.scale})`;
  syncMobileZoomTrigger(image, state);
}

function zoomToPoint(image, state, touchPoint, nextScale, animated = false) {
  const rect = image.getBoundingClientRect();
  const offsetX = touchPoint.clientX - rect.left - rect.width / 2;
  const offsetY = touchPoint.clientY - rect.top - rect.height / 2;
  const scaleFactor = nextScale - 1;

  state.scale = nextScale;
  state.translateX = -offsetX * scaleFactor;
  state.translateY = -offsetY * scaleFactor;
  applyMobileTransform(image, state, animated);
}

function resetMobileZoom(image, state) {
  state.scale = 1;
  state.translateX = 0;
  state.translateY = 0;
  state.didPan = false;
  state.movedTouch = false;
  image.style.transition = ZOOM_TRANSITION;
  image.style.transform = 'translate3d(0, 0, 0) scale(1)';
  image.style.transformOrigin = 'center center';
  syncMobileZoomTrigger(image, state);
}

function syncMobileZoomTrigger(image, state) {
  const zoomTrigger = image.closest('.product-media-container')?.querySelector('[data-mobile-zoom-trigger]');
  if (!zoomTrigger) return;
  zoomTrigger.classList.toggle('is-zoomed', state.scale > 1.01);
}

function toggleMobileZoomFromButton(image, state) {
  if (state.scale > 1.01) {
    resetMobileZoom(image, state);
    return;
  }

  const rect = image.getBoundingClientRect();
  const centerPoint = {
    clientX: rect.left + rect.width / 2,
    clientY: rect.top + rect.height / 2,
  };
  zoomToPoint(image, state, centerPoint, TAP_ZOOM_SCALE, true);
}

function initMobileZoom() {
  if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;

  const images = document.querySelectorAll('.image-magnify-hover');

  images.forEach((image) => {
    const state = createDefaultMobileState();
    mobileZoomStates.set(image, state);
    image.style.willChange = 'transform';
    const zoomTrigger = image.closest('.product-media-container')?.querySelector('[data-mobile-zoom-trigger]');
    syncMobileZoomTrigger(image, state);

    if (zoomTrigger) {
      zoomTrigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const activeState = mobileZoomStates.get(image);
        if (!activeState) return;
        toggleMobileZoomFromButton(image, activeState);
      });
    }

    image.addEventListener(
      'touchstart',
      (event) => {
        const activeState = mobileZoomStates.get(image);
        if (!activeState) return;

        if (event.touches.length === 2) {
          activeState.startDistance = distance(event.touches[0], event.touches[1]);
          activeState.startScale = activeState.scale;
          activeState.didPan = false;
          activeState.movedTouch = true;
          event.preventDefault();
          return;
        }

        if (event.touches.length !== 1) return;

        const touch = event.touches[0];
        activeState.panStartX = touch.clientX;
        activeState.panStartY = touch.clientY;
        activeState.startTranslateX = activeState.translateX;
        activeState.startTranslateY = activeState.translateY;
        activeState.startScrollY = window.scrollY;
        activeState.movedTouch = false;
      },
      { passive: false }
    );

    image.addEventListener(
      'touchmove',
      (event) => {
        const activeState = mobileZoomStates.get(image);
        if (!activeState) return;

        if (event.touches.length === 2) {
          const nextDistance = distance(event.touches[0], event.touches[1]);
          if (!activeState.startDistance) {
            activeState.startDistance = nextDistance;
            activeState.startScale = activeState.scale;
          }

          const scaleFromPinch = activeState.startScale * (nextDistance / activeState.startDistance);
          const clampedScale = clamp(scaleFromPinch, MIN_ZOOM_SCALE, MAX_ZOOM_SCALE);
          const mid = getMidPoint(event.touches[0], event.touches[1]);

          zoomToPoint(image, activeState, mid, clampedScale, false);
          activeState.didPan = true;
          activeState.movedTouch = true;
          event.preventDefault();
          return;
        }

        if (event.touches.length !== 1) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - activeState.panStartX;
        const deltaY = touch.clientY - activeState.panStartY;
        const scrollDelta = Math.abs(window.scrollY - activeState.startScrollY);

        if (
          Math.abs(deltaX) > TAP_MOVE_THRESHOLD ||
          Math.abs(deltaY) > TAP_MOVE_THRESHOLD ||
          scrollDelta > TAP_SCROLL_THRESHOLD
        ) {
          activeState.movedTouch = true;
        }

        if (activeState.scale <= 1) return;

        activeState.translateX = activeState.startTranslateX + deltaX;
        activeState.translateY = activeState.startTranslateY + deltaY;
        applyMobileTransform(image, activeState, false);
        activeState.didPan = true;
        event.preventDefault();
      },
      { passive: false }
    );

    image.addEventListener(
      'touchend',
      (event) => {
        const activeState = mobileZoomStates.get(image);
        if (!activeState) return;

        if (event.touches.length > 0) return;

        activeState.didPan = false;
        activeState.movedTouch = false;
        activeState.startDistance = 0;
      },
      { passive: false }
    );

    image.addEventListener('touchcancel', () => {
      const activeState = mobileZoomStates.get(image);
      if (!activeState) return;
      activeState.startDistance = 0;
      activeState.didPan = false;
      activeState.movedTouch = false;
    });
  });
}

function initDesktopHoverZoom(zoomRatio) {
  if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) return;

  const images = document.querySelectorAll('.image-magnify-hover');

  images.forEach((image) => {
    image.addEventListener('mouseenter', (event) => {
      getOrCreateOverlay(image);
      updateOverlayPosition(image, event, zoomRatio);
    });

    image.addEventListener('mousemove', (event) => {
      updateOverlayPosition(image, event, zoomRatio);
    });

    image.addEventListener('mouseleave', () => {
      removeOverlay(image);
    });

    image.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });
  });
}

function initZoom() {
  if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
    initDesktopHoverZoom(2);
    return;
  }

  if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
    initMobileZoom();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initZoom);
} else {
  initZoom();
}
